import { useState, type ReactNode, type SVGProps } from "react"
import { Link, router } from "@inertiajs/react"
import { useShared } from "@/lib/inertia"
import { allowanceMeter, creditTone } from "@/lib/billing"
import { Logo } from "./Logo"
import { Flash } from "./Flash"
import { ProgressBar } from "./ProgressBar"
import { cn } from "@/lib/cn"

type NavItem = {
  label: string
  href: string
  icon: (props: SVGProps<SVGSVGElement>) => ReactNode
  match?: (p: string) => boolean
  external?: boolean
}

function HomeIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M3 11.5 12 4l9 7.5M5 10v9a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function ListsIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M4 6.5h16M4 12h16M4 17.5h10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

function ContactsIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <circle cx="9" cy="10" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M3.5 19.5a5.5 5.5 0 0 1 11 0M15 7h5M15 11h4M15 15h3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

function SettingsIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function UserIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" />
      <path d="M4 21a8 8 0 0 1 16 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function MembersIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <circle cx="9" cy="9" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M3.5 19a5.5 5.5 0 0 1 11 0M16 11a3 3 0 1 0 0-6m4.5 14a5.5 5.5 0 0 0-5.5-5.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

function KeyIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M14 14a5 5 0 1 0-3.5-8.6L4 12v3h3l1-1v-2h2v-2h2l1.5-1.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function BillingIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <rect x="3" y="6" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3 11h18" stroke="currentColor" strokeWidth="1.5" />
      <path d="M7 15.5h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function ConsoleIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="m8 11 2 1.5L8 14m4 0h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ShieldIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M12 3 4 6v6c0 4.5 3.5 8 8 9 4.5-1 8-4.5 8-9V6l-8-3z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="m9 12 2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function IntegrationsIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M10 14 4 20m10-10 6-6m-6 6 4 4m-8-8-4-4m6 8 2 2m-4-4 2 2m4-4 2 2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

const primaryNav: NavItem[] = [
  { label: "Overview", href: "/app", icon: HomeIcon, match: (p) => p === "/app" },
  { label: "Lists", href: "/app/lists", icon: ListsIcon, match: (p) => p.startsWith("/app/lists") },
  { label: "Contacts", href: "/app/contacts", icon: ContactsIcon, match: (p) => p.startsWith("/app/contacts") },
]

const workspaceNav: NavItem[] = [
  { label: "General", href: "/app/settings", icon: SettingsIcon, match: (p) => p === "/app/settings" },
  { label: "Integrations", href: "/app/integrations", icon: IntegrationsIcon, match: (p) => p.startsWith("/app/integrations") },
  { label: "Members", href: "/app/members", icon: MembersIcon, match: (p) => p.startsWith("/app/members") },
  { label: "Billing", href: "/app/billing", icon: BillingIcon, match: (p) => p.startsWith("/app/billing") },
]

const developerNav: NavItem[] = [
  { label: "API keys", href: "/app/api-keys", icon: KeyIcon, match: (p) => p.startsWith("/app/api-keys") },
  { label: "Console", href: "/app/console", icon: ConsoleIcon, match: (p) => p.startsWith("/app/console") },
]

const accountNav: NavItem[] = [
  { label: "Profile", href: "/app/profile", icon: UserIcon, match: (p) => p.startsWith("/app/profile") },
  { label: "Security", href: "/app/security", icon: ShieldIcon, match: (p) => p.startsWith("/app/security") },
  { label: "Help center", href: "/help", icon: HelpIcon, external: true },
]

// AppSumo's brand mark (downloaded to /public), shown on the "Review us on
// AppSumo" pill. Rendered as an <img> so it keeps AppSumo's colours rather than
// inheriting the nav's currentColor like the SVG icons do.
function AppsumoReviewIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <img src="/appsumo-review-icon.png" alt="" width={16} height={16} className={props.className} />
  )
}

function HelpIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 16 16" fill="none" {...props}>
      <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.4" />
      <path d="M6.3 6.2a1.7 1.7 0 1 1 2.2 1.63c-.5.18-.8.6-.8 1.12v.3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="7.9" cy="11.4" r="0.55" fill="currentColor" />
    </svg>
  )
}

function MenuIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className={cn("size-5", className)} aria-hidden="true">
      <path d="M3 5h10M3 8h10M3 11h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className={cn("size-5", className)} aria-hidden="true">
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function NavLink({ item, pathname, onNavigate }: { item: NavItem; pathname: string; onNavigate?: () => void }) {
  const active = item.match ? item.match(pathname) : pathname === item.href
  const Icon = item.icon
  const className = cn(
    "group flex items-center gap-x-2.5 rounded-lg px-2.5 py-2 text-sm tracking-tight transition",
    active
      ? "bg-white/[0.08] text-white"
      : "text-ink-300 hover:bg-white/[0.05] hover:text-white",
  )
  const iconEl = (
    <Icon
      aria-hidden="true"
      className={cn(
        "size-4 shrink-0 transition",
        active ? "text-white" : "text-ink-500 group-hover:text-ink-200",
      )}
    />
  )
  const testId = `nav-${item.label.toLowerCase().replaceAll(" ", "-")}`

  // External links (e.g. the help center) open in a new tab so the user keeps
  // their place in the app.
  if (item.external) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onNavigate}
        data-testid={testId}
        className={className}
      >
        {iconEl}
        <span className="truncate">{item.label}</span>
      </a>
    )
  }

  return (
    <Link href={item.href} onClick={onNavigate} data-testid={testId} className={className}>
      {iconEl}
      <span className="truncate">{item.label}</span>
    </Link>
  )
}

function NavGroup({
  label,
  items,
  pathname,
  onNavigate,
  className,
}: {
  label?: string
  items: NavItem[]
  pathname: string
  onNavigate?: () => void
  className?: string
}) {
  return (
    <nav className={cn("flex flex-col gap-y-0.5", className)} aria-label={label ?? "Navigation"}>
      {label && (
        <p className="px-2.5 pb-1 text-[11px] font-medium tracking-wide text-ink-500 uppercase">{label}</p>
      )}
      {items.map((item) => (
        <NavLink key={item.href} item={item} pathname={pathname} onNavigate={onNavigate} />
      ))}
    </nav>
  )
}

function WorkspaceSwitcher() {
  const { auth } = useShared()
  const [open, setOpen] = useState(false)
  if (!auth.workspaces || auth.workspaces.length === 0) return null
  const current = auth.workspace

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="workspace-switcher"
        className="flex w-full items-center justify-between gap-x-2 rounded-xl bg-ink-800/60 px-3 py-2 text-left text-sm ring-1 ring-ink-700 hover:bg-ink-800 hover:ring-ink-600"
      >
        <span className="flex min-w-0 items-center gap-x-2.5">
          <span
            aria-hidden="true"
            className="grid size-8 shrink-0 place-items-center rounded-lg bg-brand-500 text-xs font-semibold text-ink-950 ring-1 ring-brand-400/40"
          >
            {current?.name?.charAt(0)?.toUpperCase() ?? "C"}
          </span>
          <span className="min-w-0">
            <span className="block truncate font-medium tracking-tight text-white">
              {current?.name ?? "Workspace"}
            </span>
            <span className="block truncate text-xs text-ink-400 tabular-nums">
              {(auth.workspace?.credits_remaining ?? 0).toLocaleString()} credits
            </span>
          </span>
        </span>
        <svg viewBox="0 0 16 16" className="size-4 shrink-0 text-ink-400" aria-hidden="true">
          <path d="M5 6.5L8 10l3-3.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          onMouseLeave={() => setOpen(false)}
          className="absolute right-0 left-0 z-50 mt-2 overflow-hidden rounded-xl bg-ink-900 p-1.5 ring-1 ring-ink-700 shadow-lg shadow-black/40"
        >
          {auth.workspaces.map((w) => (
            <button
              key={w.id}
              type="button"
              role="menuitem"
              onClick={() => router.post(`/app/switch/${w.slug}`)}
              className={cn(
                "flex w-full items-center justify-between gap-x-3 rounded-lg px-2.5 py-2 text-left text-sm",
                w.id === current?.id
                  ? "bg-white/[0.08] text-white"
                  : "text-ink-300 hover:bg-white/[0.05] hover:text-white",
              )}
            >
              <span className="truncate">{w.name}</span>
              {w.id === current?.id && (
                <svg viewBox="0 0 16 16" className="size-4 text-brand-400" aria-hidden="true">
                  <path
                    d="M3.5 8.5l3 3 6-7"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Verification allowance (green) — only shown when the workspace has a plan.
// Deliberately distinct in colour from credits so the two meters never blur.
function VerificationMeter() {
  const { auth } = useShared()
  const sub = auth.workspace?.subscription
  if (!sub) return null

  const { total, remaining, usedPct } = allowanceMeter(sub.monthly_allowance, sub.allowance_remaining)
  const renews = sub.renews_at ? new Date(sub.renews_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null

  return (
    <div data-testid="verification-meter" className="rounded-xl bg-ink-800/60 px-3 py-3 ring-1 ring-ink-700">
      <div className="flex items-baseline justify-between gap-x-2">
        <p className="inline-flex items-center gap-x-1.5 text-xs font-medium tracking-tight text-ink-300">
          <span aria-hidden="true" className="size-1.5 rounded-full bg-brand-500" />
          Verifications
        </p>
        <p className="font-display text-sm font-semibold tracking-tight tabular-nums text-white">
          {remaining.toLocaleString()}
          <span className="text-ink-400"> / {total.toLocaleString()}</span>
        </p>
      </div>
      <ProgressBar
        value={usedPct}
        tone="brand"
        size="sm"
        className="mt-2"
        trackClassName="bg-ink-700"
        ariaLabel="Monthly verifications used"
      />
      <p className="mt-2 text-[11px] tracking-tight text-ink-400 tabular-nums">
        {sub.name}
        {renews ? ` · resets ${renews}` : ""}
      </p>
    </div>
  )
}

// Credits (violet) — enrichment + verification overage. Distinct from the green
// verification meter above.
function CreditsMeter() {
  const { auth } = useShared()
  const hasPlan = !!auth.workspace?.subscription
  const remaining = auth.workspace?.credits_remaining ?? 0
  // We don't have a "granted" total in shared props — show usage as remaining/2000 baseline if unknown.
  const granted = Math.max(remaining, 2000)
  const usedPct = granted > 0 ? Math.min(100, Math.round(((granted - remaining) / granted) * 100)) : 0
  const tone = creditTone(remaining)

  return (
    <div data-testid="credits-meter" className="rounded-xl bg-ink-800/60 px-3 py-3 ring-1 ring-ink-700">
      <div className="flex items-baseline justify-between gap-x-2">
        <p className="inline-flex items-center gap-x-1.5 text-xs font-medium tracking-tight text-ink-300">
          <span aria-hidden="true" className="size-1.5 rounded-full bg-violet-500" />
          Credits
        </p>
        <p className="font-display text-sm font-semibold tracking-tight tabular-nums text-white">
          {remaining.toLocaleString()}
        </p>
      </div>
      <ProgressBar
        value={usedPct}
        tone={tone}
        size="sm"
        className="mt-2"
        trackClassName="bg-ink-700"
        ariaLabel="Credits used"
      />
      <div className="mt-2 flex items-center justify-between gap-x-2">
        <p className="text-[11px] tracking-tight text-ink-400">{hasPlan ? "Enrichment & overage" : "For everything"}</p>
        <Link
          href="/app/billing"
          className="inline-flex items-center gap-x-1 text-xs font-medium tracking-tight text-ink-300 hover:text-white"
        >
          {hasPlan ? "Top up →" : "Plans & credits →"}
        </Link>
      </div>
    </div>
  )
}

export default function AppLayout({ children }: { children: ReactNode }) {
  const { pathname, auth } = useShared()
  const [mobileOpen, setMobileOpen] = useState(false)
  const firstName = auth.user?.name?.split(" ")[0]

  // AppSumo customers get a "Review us on AppSumo" pill in the Account group —
  // the review link travels with the subscription so it stays in sync with the
  // configured listing.
  const sub = auth.workspace?.subscription
  const accountItems: NavItem[] =
    sub?.source === "appsumo" && sub.review_url
      ? [
          ...accountNav,
          { label: "Review us on AppSumo", href: sub.review_url, icon: AppsumoReviewIcon, external: true },
        ]
      : accountNav

  return (
    <div className="isolate flex min-h-dvh flex-col bg-ink-50/40 text-ink-950 lg:flex-row">
      <Flash />

      {/* Mobile topbar */}
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-ink-800 bg-ink-950 px-4 py-3 lg:hidden">
        <Link href="/app" aria-label="CleanMyList dashboard" className="inline-flex items-center">
          <Logo className="text-xl text-white" />
        </Link>
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          className="inline-flex size-9 items-center justify-center rounded-lg text-ink-200 hover:bg-ink-800"
        >
          {mobileOpen ? <CloseIcon /> : <MenuIcon />}
        </button>
      </header>

      {/* Sidebar */}
      <aside
        className={cn(
          "z-20 w-full shrink-0 border-ink-800 bg-ink-950 text-white lg:sticky lg:top-0 lg:flex lg:h-dvh lg:w-72 lg:flex-col lg:overflow-y-auto lg:border-r",
          mobileOpen ? "block" : "hidden lg:block",
        )}
      >
        <div className="flex h-full flex-col gap-y-6 px-4 py-6 lg:px-5 lg:py-7">
          <div className="hidden px-1 lg:block">
            <Link href="/app" aria-label="CleanMyList dashboard" className="inline-flex items-center">
              <Logo className="text-white" />
            </Link>
          </div>

          <WorkspaceSwitcher />

          <div className="flex flex-col gap-y-5">
            <NavGroup items={primaryNav} pathname={pathname} onNavigate={() => setMobileOpen(false)} />
            <NavGroup label="Workspace" items={workspaceNav} pathname={pathname} onNavigate={() => setMobileOpen(false)} />
            <NavGroup label="Developer" items={developerNav} pathname={pathname} onNavigate={() => setMobileOpen(false)} />
          </div>

          <div className="mt-auto flex flex-col gap-y-3">
            <VerificationMeter />
            <CreditsMeter />
            {auth.user?.superadmin && (
              <Link
                href="/admin"
                data-testid="superadmin-entry"
                className="flex items-center gap-x-2 rounded-lg bg-brand-500/15 px-2.5 py-2 text-sm font-medium text-brand-200 ring-1 ring-brand-500/30 hover:bg-brand-500/25 hover:text-brand-100"
              >
                <svg viewBox="0 0 24 24" className="size-4 text-brand-300" fill="none" aria-hidden="true">
                  <path d="M12 3 4 6v6c0 4.5 3.5 8 8 9 4.5-1 8-4.5 8-9V6l-8-3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                  <path d="m9 12 2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>Superadmin</span>
              </Link>
            )}
            <NavGroup
              label="Account"
              items={accountItems}
              pathname={pathname}
              onNavigate={() => setMobileOpen(false)}
              className="border-t border-ink-800 pt-4"
            />
            <div className="flex items-center gap-x-2.5 rounded-xl bg-ink-800/60 px-3 py-2 ring-1 ring-ink-700">
              <span
                aria-hidden="true"
                className="grid size-8 shrink-0 place-items-center rounded-full bg-brand-500 text-xs font-semibold text-ink-950"
              >
                {firstName?.charAt(0)?.toUpperCase() ?? "?"}
              </span>
              <div className="min-w-0 grow">
                <p className="truncate text-sm font-medium tracking-tight text-white">{auth.user?.name}</p>
                <p className="truncate text-xs text-ink-400">{auth.user?.email}</p>
              </div>
              <Link
                href="/sign-out"
                method="delete"
                as="button"
                aria-label="Sign out"
                className="grid size-7 shrink-0 place-items-center rounded-lg text-ink-300 hover:bg-ink-800 hover:text-white"
              >
                <svg viewBox="0 0 24 24" className="size-4" fill="none" aria-hidden="true">
                  <path
                    d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l5-5-5-5M15 12H4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 lg:px-10 lg:py-10">{children}</div>
      </main>
    </div>
  )
}
